import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'hi' : 'en';
    i18n.changeLanguage(newLang);
  };

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/services', label: t('nav.services') },
    { to: '/industries', label: t('nav.industries') },
    { to: '/working-model', label: t('nav.working_model') },
    { to: '/legal', label: t('nav.legal') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const isAdmin = location.pathname.startsWith('/admin');
  if (isAdmin) return null;

  return (
    <>
      {/* Background blur overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu-overlay"
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      <nav
        data-testid="main-navbar"
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled ? 'bg-[#0B1C2D]/98 backdrop-blur-lg border-b border-white/10 shadow-lg shadow-black/20' : 'bg-gradient-to-b from-[#0B1C2D]/80 to-transparent'
        }`}
      >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between py-4 md:py-0 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity duration-200">
            <img 
              src="/kapish-logo.png" 
              alt="KAPISH Corporate Resolution LLP" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.slice(1) || 'home'}`}
                className={`px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-all duration-200 relative group ${
                  location.pathname === link.to
                    ? 'text-secondary'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {link.label}
                {/* Underline animation */}
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 bg-secondary transition-all duration-300 ${
                    location.pathname === link.to ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}
                  style={{ transformOrigin: location.pathname === link.to ? 'left' : 'left' }}
                />
              </Link>
            ))}
          </div>

          {/* Language Toggle & Mobile Menu */}
          <div className="flex items-center space-x-2 md:space-x-3">
            <Button
              data-testid="language-toggle"
              onClick={toggleLanguage}
              variant="ghost"
              className="text-gray-300 hover:text-secondary hover:bg-white/5 font-semibold text-sm transition-all duration-200"
              size="sm"
            >
              {i18n.language === 'en' ? 'हिंदी' : 'English'}
            </Button>

            {/* Mobile Menu Toggle */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white hover:bg-white/10 rounded-md transition-colors duration-200"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div data-testid="mobile-menu" className="lg:hidden pb-4 border-t border-white/10">
            <div className="space-y-2 pt-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-2.5 text-sm font-semibold uppercase tracking-wide rounded-md transition-all duration-200 ${
                    location.pathname === link.to
                      ? 'text-secondary bg-white/10'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};
